/**
 * `validate/url-validate` — URL syntax validator.
 *
 * Different scope from `network/url-parse`: that page assumes a
 * legal URL and exposes its components for inspection. THIS page
 * answers "is this string a valid URL, and if not, why?". The two
 * tools intentionally complement instead of duplicating.
 *
 * Implementation: try the WHATWG `URL` constructor, then layer on
 * project-specific checks:
 *
 *   - protocol whitelist (default: http https ws wss ftp mailto)
 *   - empty hostname (URL allows `mailto:` and other schemeful URIs
 *     to omit hostname; we surface that explicitly)
 *   - require a TLD when `requireTld` is on (rejects bare host
 *     names like `http://localhost` and `http://my-server`)
 *   - default-port redundancy (a soft warning, not a hard fail)
 *
 * The validator is mode-agnostic: we don't try to "fix" the URL;
 * we tell the user what's wrong and let them edit.
 */

export interface ValidationIssue {
  severity: 'error' | 'warn';
  code: string;
  message: string;
}

export interface UrlParts {
  protocol: string;
  username: string;
  password: string;
  host: string;
  hostname: string;
  port: string;
  pathname: string;
  search: string;
  hash: string;
  origin: string;
}

export interface UrlValidation {
  ok: boolean;
  parts?: UrlParts;
  issues: ValidationIssue[];
}

export interface UrlValidationOpts {
  /** Allowed protocols (without the trailing colon). */
  schemes?: string[];
  /** When true, reject http(s) URLs whose hostname has no dot. */
  requireTld?: boolean;
}

const DEFAULT_SCHEMES = ['http', 'https', 'ws', 'wss', 'ftp', 'mailto'];

const DEFAULT_PORT_FOR: Record<string, string> = {
  'http:': '80',
  'https:': '443',
  'ws:': '80',
  'wss:': '443',
  'ftp:': '21',
};

const HOSTLESS_SCHEMES = new Set(['mailto:', 'tel:', 'data:', 'javascript:']);

export function validateUrl(
  input: string,
  opts: UrlValidationOpts = {},
): UrlValidation {
  const issues: ValidationIssue[] = [];
  const trimmed = input.trim();

  if (!trimmed) {
    return {
      ok: false,
      issues: [{ severity: 'error', code: 'empty', message: 'URL 为空' }],
    };
  }
  if (trimmed !== input) {
    issues.push({
      severity: 'warn',
      code: 'whitespace',
      message: '存在前后空白字符（已自动忽略）',
    });
  }

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    issues.push({
      severity: 'error',
      code: 'syntax',
      message: 'WHATWG URL 解析失败：可能是缺少协议或包含非法字符',
    });
    return { ok: false, issues };
  }

  const schemes = opts.schemes ?? DEFAULT_SCHEMES;
  const protoNoColon = url.protocol.slice(0, -1);
  if (!schemes.includes(protoNoColon)) {
    issues.push({
      severity: 'error',
      code: 'scheme-not-allowed',
      message: `协议 ${url.protocol} 不在允许列表（${schemes.join(', ')}）`,
    });
  }

  if (!url.hostname && !HOSTLESS_SCHEMES.has(url.protocol)) {
    issues.push({
      severity: 'error',
      code: 'no-host',
      message: '缺少主机名',
    });
  }

  if (
    opts.requireTld &&
    url.hostname &&
    !HOSTLESS_SCHEMES.has(url.protocol) &&
    !url.hostname.includes('.')
  ) {
    issues.push({
      severity: 'error',
      code: 'no-tld',
      message: '主机名缺少顶级域名（.com / .net / ...）',
    });
  }

  // The WHATWG URL constructor strips the default port when it
  // matches the protocol's default. To detect "user wrote `:80`
  // explicitly", inspect the trimmed input directly.
  const explicitDefaultPort =
    DEFAULT_PORT_FOR[url.protocol] &&
    new RegExp(
      `^${protoNoColon}://[^/?#]*?:${DEFAULT_PORT_FOR[url.protocol]}(?:[/?#]|$)`,
      'i',
    ).test(trimmed);
  if (explicitDefaultPort) {
    issues.push({
      severity: 'warn',
      code: 'default-port',
      message: `URL 显式包含 ${url.protocol} 的默认端口（${
        DEFAULT_PORT_FOR[url.protocol]
      }），可省略`,
    });
  }

  if (url.username || url.password) {
    issues.push({
      severity: 'warn',
      code: 'userinfo',
      message: 'URL 包含用户名 / 密码，多数浏览器与服务端会拒绝或剥离',
    });
  }

  // Whitespace inside the input that survived URL normalisation
  // (e.g. raw spaces in query) almost always means a typo.
  if (/\s/.test(trimmed)) {
    issues.push({
      severity: 'warn',
      code: 'inner-whitespace',
      message: 'URL 内部包含空白字符，应通过 percent-encoding 转义',
    });
  }

  const ok = !issues.some((i) => i.severity === 'error');
  if (!ok) return { ok, issues };

  return {
    ok,
    issues,
    parts: {
      protocol: url.protocol,
      username: url.username,
      password: url.password,
      host: url.host,
      hostname: url.hostname,
      port: url.port,
      pathname: url.pathname,
      search: url.search,
      hash: url.hash,
      origin: url.origin,
    },
  };
}

export const URL_DEFAULT_SCHEMES = DEFAULT_SCHEMES;
